/**
 * NewVersionWatcher — an open tab picks up a new deploy by itself.
 *
 * Checks the server's build every minute and whenever the tab comes back into
 * view. When it is newer:
 *   - the NEXT screen change reloads the tab — a safe moment, nothing is
 *     half-typed on the screen being opened (Kasish opening the board);
 *   - a small bar offers "Reload" at once for anyone staying on one screen.
 * It never reloads in the middle of a screen on its own.
 *
 * 🔒 One automatic reload per version (sessionStorage), so a server that keeps
 * answering a different entry can never loop the tab. See utils/newVersion.ts.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchServedEntry, isNewVersion, runningEntry } from '../utils/newVersion';

export const CHECK_EVERY_MS = 60_000;
const RELOADED_FOR_KEY = 'aura-version-reload-for';

function alreadyReloadedFor(entry: string): boolean {
  try { return sessionStorage.getItem(RELOADED_FOR_KEY) === entry; } catch { return false; }
}

function reloadFor(entry: string): void {
  try { sessionStorage.setItem(RELOADED_FOR_KEY, entry); } catch { /* private mode: reload anyway */ }
  window.location.reload();
}

export function NewVersionWatcher() {
  const [served, setServed] = useState<string | null>(null);
  const running = useRef<string | null>(null);
  const location = useLocation();
  const firstPath = useRef(location.pathname);

  useEffect(() => {
    running.current = runningEntry();
    if (!running.current) return; // dev server / not a built page: stay off
    let alive = true;
    const check = async () => {
      const e = await fetchServedEntry();
      if (alive && isNewVersion(running.current, e)) setServed(e);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    const id = window.setInterval(() => { void check(); }, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      alive = false;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  // The next screen change after a new version is seen reloads the tab.
  useEffect(() => {
    if (location.pathname === firstPath.current) return;
    firstPath.current = location.pathname;
    if (served && !alreadyReloadedFor(served)) reloadFor(served);
  }, [location.pathname, served]);

  if (!served) return null;
  return (
    <div
      data-testid="new-version-bar"
      role="status"
      style={{
        position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 10000,
        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px 8px 14px',
        background: 'var(--ink-0, #1f2a24)', color: '#fff', borderRadius: 10,
        boxShadow: '0 6px 24px rgba(0,0,0,.18)', fontSize: 13,
      }}
    >
      <span>A new version of AURA is ready.</span>
      <button
        type="button"
        onClick={() => reloadFor(served)}
        style={{
          border: 0, borderRadius: 7, padding: '5px 12px', cursor: 'pointer',
          background: '#fff', color: 'var(--ink-0, #1f2a24)', fontWeight: 600, fontSize: 12.5,
        }}
      >
        Reload
      </button>
    </div>
  );
}
